/**
 * @file ExportTableButton.js
 * @version 2.0.2
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-08-26
 * @modified 2026-10-15
 * @description "Export PDF" for a search screen's current results.
 *
 *              Opens TableExportModal rather than downloading immediately. It
 *              used to POST straight to the renderer, which meant every search
 *              screen printed A4 landscape at whatever size the rows came out —
 *              no orientation, no fit, no sight of the document until it landed
 *              in the downloads folder. The blend and formulation exports had
 *              had that modal for a while; there was no reason the table
 *              exports should be the ones without it, and one component change
 *              gives it to all six screens at once.
 *
 *              build() is still called at CLICK time, not on every render — a
 *              screen with 200 rows should not be formatting all of them into
 *              strings on the chance somebody presses this, and the PO export
 *              fetches line items, which must not happen speculatively either.
 *              The modal calls it once, when it opens.
 *
 *              Disabled with a reason when there is nothing to export. An
 *              enabled button that produces an empty PDF is a worse answer than
 *              one that says why it cannot.
 */

import React, { useState } from 'react';
import TableExportModal from './TableExportModal';
import { showAlert } from './Alert';
import { useSlideOutButton } from './SlideOutButton';

function ExportTableButton({
  build,
  rowCount = 0,
  label = 'Export PDF',
  user,
  title,
  modalTitle,
  summary,
  defaultOrientation = 'portrait',
  onError
}) {
  const [open, setOpen] = useState(false);

  const disabled = rowCount === 0;
  // Measured where it mounts (CLAUDE.md), not left to the screen's header.
  useSlideOutButton([label, disabled]);

  return (
    <>
      <button
        className="btn-slideout"
        onClick={() => setOpen(true)}
        disabled={disabled}
        title={disabled
          ? 'Nothing to export — search for something first'
          : `Export these ${rowCount} rows as a PDF`}
      >
        <div className="icon-wrapper">
          <span className="material-symbols-rounded">ios_share</span>
        </div>
        <span className="btn-label">{label}</span>
      </button>

      {open && (
        <TableExportModal
          build={build}
          user={user}
          title={title}
          modalTitle={modalTitle}
          summary={summary}
          defaultOrientation={defaultOrientation}
          onClose={() => setOpen(false)}
          onDone={(message) => {
            setOpen(false);
            if (message) showAlert(message, 'success');
          }}
        />
      )}
    </>
  );
}

export default ExportTableButton;
